/**
 * Incoming P2P call overlay (web).
 *
 * Shown when a call arrives while the user is NOT already on a call — the
 * ordinary "someone is calling you" case. Deaf users can't hear a ring, so
 * while this is visible the page edge flashes at 1 Hz (steady with reduced
 * motion), the tab title flashes and a browser notification fires (see
 * deafAccessibleAlert). The takeover shows the caller's name first, then
 * "Video call", then one-tap Decline · VideoMail · Answer (web-vrs.html,
 * approved 2026-10-29). VideoMail only where the server can give the caller
 * the VideoMail offer (ringPresentation.ts canSendRingToVideoMail).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { keyframes } from 'tss-react';
import { makeStyles } from 'tss-react/mui';
import { getLogoWhiteUrl, getTenantId, isFeatureEnabled } from '../../base/whitelabel/functions';
import { queueService } from '../../interpreter-queue/InterpreterQueueService';
import { clearIncomingP2PCall } from '../actions';
import { startCallAttention } from '../deafAccessibleAlert';
import { canSendRingToVideoMail, ringBrandMark } from './ringPresentation';
/** How long a direct-call Answer may go unconfirmed before the card offers it again. */
const RESPONSE_TIMEOUT_MS = 10000;
/** How long "Answered on another device." stays before the card goes. */
const ENDED_NOTICE_MS = 2000;
/** The server's one-winner refusals (docs/single-winner-answers.md). */
const ANSWER_LOST = ['ANSWERED_ELSEWHERE', 'ALREADY_TAKEN'];
function callRef(data) {
    if (!data || typeof data !== 'object')
        return {};
    const record = data;
    return {
        callId: typeof record.callId === 'string' ? record.callId : undefined,
        invitationId: typeof record.invitationId === 'string' ? record.invitationId : undefined
    };
}
const useStyles = makeStyles()(theme => {
    return {
        // The page edge flashes once a second: the ring a Deaf person sees
        // from across the room. With reduced motion it stays on, steady.
        edge: {
            animation: `${keyframes `
                0% { opacity: 1; }
                100% { opacity: 0.15; }
            `} 1s steps(2, jump-none) infinite`,
            border: `5px solid ${theme.palette.action01}`,
            inset: 0,
            pointerEvents: 'none',
            position: 'fixed',
            zIndex: 10001,
            '@media (prefers-reduced-motion: reduce)': {
                animation: 'none'
            }
        },
        // The name is the largest text (it beats the card's h3 rule).
        name: {
            '&&': {
                color: theme.palette.text01,
                fontSize: 28,
                fontWeight: 600,
                margin: '0 0 2px',
                overflowWrap: 'anywhere'
            }
        },
        kind: {
            color: theme.palette.text02,
            fontSize: 14,
            margin: '4px 0 0'
        },
        videoMail: {
            background: theme.palette.ui03
        },
        // No photo: the brand's mark in a ring, as on the phone apps' ring.
        brandMark: {
            alignItems: 'center',
            border: '2px solid rgba(255, 255, 255, 0.18)',
            borderRadius: '50%',
            boxSizing: 'border-box',
            display: 'flex',
            height: 132,
            justifyContent: 'center',
            margin: '0 auto 16px',
            padding: 24,
            width: 132,
            '& img': {
                maxHeight: '100%',
                maxWidth: '100%',
                objectFit: 'contain'
            }
        }
    };
});
const IncomingCallOverlay = () => {
    const { classes, cx } = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const incoming = useSelector((state) => state['features/call-management']?.incomingP2PCall);
    const [pending, setPending] = useState(false);
    const [error, setError] = useState('');
    const [unavailable, setUnavailable] = useState(false);
    const [ended, setEnded] = useState(false);
    const [photoFailed, setPhotoFailed] = useState();
    const incomingRef = useRef(incoming);
    incomingRef.current = incoming;
    const stopAttentionRef = useRef(null);
    const pendingRef = useRef(false);
    const attemptedRef = useRef(false);
    const answerTimerRef = useRef(undefined);
    useEffect(() => {
        incomingRef.current = incoming;
        pendingRef.current = false;
        attemptedRef.current = false;
        setPending(false);
        setError('');
        setUnavailable(false);
        setEnded(false);
        if (!incoming) {
            return undefined;
        }
        const stop = startCallAttention({
            callerName: incoming.callerName || t('vrs.ring.unknownCaller'),
            body: 'Incoming video call'
        });
        stopAttentionRef.current = stop;
        let endedTimer;
        const matches = (data) => data.callId === incoming.callId
            && (!data.invitationId || data.invitationId === incoming.invitationId);
        const clear = (data) => {
            if (matches(data))
                dispatch(clearIncomingP2PCall());
        };
        // Cancel/end payloads are typed loosely on the service; read only the ids.
        const gone = (data) => clear(callRef(data));
        // One answer wins a ring (docs/single-winner-answers.md): another
        // device of this person answered first. Say so, then the card goes.
        const lost = () => {
            if (endedTimer)
                return;
            clearTimeout(answerTimerRef.current);
            pendingRef.current = false;
            stop();
            setPending(false);
            setError('');
            setEnded(true);
            endedTimer = setTimeout(() => dispatch(clearIncomingP2PCall()), ENDED_NOTICE_MS);
        };
        const failed = (data) => {
            if (!incoming.conferenceInvitation || !matches(data) || data.invitationId !== incoming.invitationId)
                return;
            if (data.code && ANSWER_LOST.includes(data.code)) {
                lost();
                return;
            }
            pendingRef.current = false;
            setPending(false);
            setUnavailable(data.code !== 'RESPONSE_TIMEOUT' && data.code !== 'INVITATION_FAILED');
            setError(data.message || t('vrs.ring.notConfirmed'));
        };
        // A direct-call Answer is a request, not an admission. When the server
        // refuses it (call already ended, not a participant) the refusal used
        // to vanish into the console while the card kept ringing.
        const refused = (data) => {
            if (incoming.conferenceInvitation || data.retrying)
                return;
            const { callId } = callRef(data);
            if (callId && callId !== incoming.callId)
                return;
            if (data.code && ANSWER_LOST.includes(data.code) && (pendingRef.current || callId)) {
                lost();
                return;
            }
            if (!pendingRef.current)
                return;
            clearTimeout(answerTimerRef.current);
            pendingRef.current = false;
            setPending(false);
            setUnavailable(data.code === 'NOT_FOUND' || data.code === 'FORBIDDEN');
            setError(data.message || t('vrs.ring.notConfirmed'));
        };
        // The ring ended on this person's devices (vrs-server/ws/ring-ledger.js).
        // The device that won is never told, so an answer still on its way here
        // lost the race; a timeout or another device's decline leaves it to its
        // own outcome.
        const ringEnded = (data) => {
            // Without a call id, a conference ring is named by its invitation.
            if (!(data.callId ? matches(data) : data.ringId === incoming.invitationId?.toUpperCase()))
                return;
            if (data.reason === 'answered_elsewhere') {
                lost();
                return;
            }
            if (pendingRef.current && (data.reason === 'timeout' || data.reason === 'declined_elsewhere'))
                return;
            dispatch(clearIncomingP2PCall());
        };
        // An interpreter's call-back answered: the server holds this person
        // until the one the interpreter was with is back. That is an answer.
        const held = (data) => {
            if (callRef(data).callId === incoming.callId)
                clearTimeout(answerTimerRef.current);
        };
        queueService.on('p2pJoinRoom', clear);
        queueService.on('callEnded', clear);
        // The caller hung up or lost the network while this was ringing.
        // Mobile clears its sheet on these; the web card used to ring on.
        queueService.on('p2pCallCancelled', gone);
        queueService.on('p2pCallEnded', gone);
        queueService.on('conferenceInvitationUpdate', clear);
        queueService.on('conferenceInvitationError', failed);
        queueService.on('error', refused);
        queueService.on('ringEnded', ringEnded);
        queueService.on('anchorHold', held);
        // Every ring has a window (the server's still-active reminder is 30 s);
        // a card that outlives it is a ghost.
        const expires = incoming.expiresAt ? Date.parse(incoming.expiresAt) : Date.now() + 30000;
        const timeout = setTimeout(() => {
            if (!attemptedRef.current) {
                stop();
                dispatch(clearIncomingP2PCall());
            }
        }, Math.max(0, Math.min(30000, expires - Date.now())));
        return () => {
            incomingRef.current = null;
            stopAttentionRef.current = null;
            stop();
            clearTimeout(timeout);
            clearTimeout(answerTimerRef.current);
            clearTimeout(endedTimer);
            queueService.off('p2pJoinRoom', clear);
            queueService.off('callEnded', clear);
            queueService.off('p2pCallCancelled', gone);
            queueService.off('p2pCallEnded', gone);
            queueService.off('conferenceInvitationUpdate', clear);
            queueService.off('conferenceInvitationError', failed);
            queueService.off('error', refused);
            queueService.off('ringEnded', ringEnded);
            queueService.off('anchorHold', held);
        };
    }, [incoming?.callId, incoming?.invitationId, dispatch]);
    if (!incoming) {
        return null;
    }
    const callerName = incoming.callerName || t('vrs.ring.unknownCaller');
    const videoMail = canSendRingToVideoMail(incoming, isFeatureEnabled('voicemail'));
    // Someone with a photo fills the screen with it, as a phone shows a
    // contact's (vrs-layout/profilePhoto.ts; the room page is on the API
    // origin, so the link is used as it is).
    const photo = incoming.callerPhotoUrl && photoFailed !== incoming.callerPhotoUrl
        ? incoming.callerPhotoUrl : undefined;
    const onPhotoError = () => setPhotoFailed(photo);
    const handleAnswer = () => {
        if (pendingRef.current || unavailable || ended || incomingRef.current !== incoming)
            return;
        stopAttentionRef.current?.();
        setError('');
        if (!incoming.inboundVrs) {
            // Stay on screen as "Joining…" until p2p_join_room admits us (or
            // the server refuses); inbound VRS answers navigate immediately.
            attemptedRef.current = true;
            pendingRef.current = true;
            setPending(true);
        }
        try {
            if (incoming.inboundVrs) {
                // A hearing caller dialed this client's VRS number: there is no
                // p2p call to accept — join the queued room directly through the
                // same path a p2p_join_room event takes. Sending p2p_accept here
                // would carry a queue requestId and a phone number where the
                // server expects call/user ids.
                queueService.announceLocalRoomJoin({
                    callId: incoming.callId,
                    callerName: incoming.callerName,
                    clientStartWithVideoMuted: incoming.clientStartWithVideoMuted,
                    inboundVrs: true,
                    roomName: incoming.roomName
                });
            }
            else {
                queueService.acceptP2PCall({
                    callId: incoming.callId,
                    callerId: incoming.callerId,
                    conferenceInvitation: incoming.conferenceInvitation,
                    invitationId: incoming.invitationId,
                    roomName: incoming.roomName
                });
                // Nothing confirmed a direct answer in time: Answer comes back
                // (a conference answer has the service's own timer).
                if (!incoming.conferenceInvitation) {
                    clearTimeout(answerTimerRef.current);
                    answerTimerRef.current = setTimeout(() => {
                        if (!pendingRef.current || incomingRef.current !== incoming)
                            return;
                        pendingRef.current = false;
                        setPending(false);
                        setError(t('vrs.ring.notConfirmed'));
                    }, RESPONSE_TIMEOUT_MS);
                }
            }
            if (incoming.inboundVrs)
                dispatch(clearIncomingP2PCall());
        }
        catch {
            pendingRef.current = false;
            setPending(false);
            setError(t('vrs.ring.notSent'));
        }
    };
    // The portal's "Send to VideoMail": a decline that gives the caller the
    // VideoMail offer at once (ws/p2p-handlers.js handleP2PDecline).
    const handleVideoMail = () => {
        if (ended || pendingRef.current || incomingRef.current !== incoming)
            return;
        try {
            queueService.declineP2PCall({
                callId: incoming.callId,
                callerId: incoming.callerId,
                sendToVoicemail: true
            });
            dispatch(clearIncomingP2PCall());
        }
        catch {
            setError(t('vrs.ring.notSent'));
        }
    };
    const handleDecline = () => {
        if (ended || incomingRef.current !== incoming)
            return;
        // Inbound VRS rings have no p2p call to decline — the queued request
        // times out server-side; just dismiss the overlay.
        try {
            if (!incoming.inboundVrs) {
                queueService.declineP2PCall({
                    callId: incoming.callId,
                    callerId: incoming.callerId,
                    conferenceInvitation: incoming.conferenceInvitation,
                    invitationId: incoming.invitationId
                });
            }
            dispatch(clearIncomingP2PCall());
        }
        catch {
            setError(t('vrs.ring.notSent'));
        }
    };
    return (React.createElement("div", { "aria-label": t('vrs.ring.calling', { name: callerName }), className: photo ? 'call-waiting-overlay has-photo' : 'call-waiting-overlay', role: 'alertdialog' },
        React.createElement("div", { "aria-hidden": 'true', className: cx('call-ring-edge', classes.edge) }),
        photo ? (React.createElement("div", { "aria-hidden": 'true', className: 'call-waiting-photo' },
            React.createElement("img", { alt: '', decoding: 'async', onError: onPhotoError, src: photo }))) : null,
        React.createElement("div", { className: 'call-waiting-card' },
            photo ? null : (React.createElement("div", { "aria-hidden": 'true', className: cx('call-ring-brand', classes.brandMark) },
                React.createElement("img", { alt: '', src: ringBrandMark(getTenantId(), getLogoWhiteUrl()) }))),
            React.createElement("h3", { className: cx('call-waiting-caller', classes.name) }, callerName),
            React.createElement("p", { className: classes.kind }, incoming.groupCall ? t('vrs.ring.groupCall') : t('vrs.ring.videoCall')),
            React.createElement("p", { "aria-live": 'polite', className: 'call-waiting-status' }, ended ? t('vrs.ring.answeredElsewhere') : error || (pending ? t('vrs.ring.joining') : '')),
            React.createElement("div", { className: 'call-waiting-actions' },
                React.createElement("button", { className: 'call-waiting-btn reject', disabled: ended, onClick: handleDecline, type: 'button' },
                    React.createElement("i", { className: 'icon-phone-hangup' }),
                    React.createElement("span", null, t('vrs.ring.decline'))),
                videoMail && (React.createElement("button", { className: cx(classes.videoMail, 'call-waiting-btn videomail'), disabled: pending || ended, onClick: handleVideoMail, type: 'button' },
                    React.createElement("span", null, t('vrs.ring.videoMail')))),
                React.createElement("button", { "aria-busy": pending, className: 'call-waiting-btn accept', disabled: pending || unavailable || ended, onClick: handleAnswer, type: 'button' },
                    React.createElement("i", { className: 'icon-phone' }),
                    React.createElement("span", null, pending ? t('vrs.ring.joining') : t('vrs.ring.answer')))))));
};
export default IncomingCallOverlay;
